import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";
import { privacyConsentService } from "../services/privacy-consent.service";
import type { ConsentPreferences, StoredConsent } from "../legal/legal.types";

type PrivacyConsentContextValue = {
  consent: StoredConsent | null;
  preferences: ConsentPreferences;
  hasMadeChoice: boolean;
  canUseFunctionalStorage: boolean;
  canUseAnalytics: boolean;
  canUseMarketing: boolean;
  saveConsent: (preferences: ConsentPreferences) => void;
  acceptEssentialOnly: () => void;
  acceptRecommended: () => void;
  acceptAll: () => void;
  clearLocalDrafts: () => void;
  clearConsentAndPreferences: () => void;
};

const PrivacyConsentContext = createContext<PrivacyConsentContextValue | null>(null);

const subscribe = (callback: () => void) => {
  const events = ["lrnsy:privacy-consent-updated", "lrnsy:privacy-consent-cleared", "storage"];

  events.forEach((event) => window.addEventListener(event, callback));
  return () => events.forEach((event) => window.removeEventListener(event, callback));
};

const getSnapshot = () => {
  return JSON.stringify(privacyConsentService.getConsent());
};

export function PrivacyConsentProvider({ children }: { children: React.ReactNode }) {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const consent = privacyConsentService.getConsent();
  const preferences = privacyConsentService.getPreferences();

  const saveConsent = useCallback((next: ConsentPreferences) => {
    privacyConsentService.saveConsent(next);
  }, []);

  const acceptEssentialOnly = useCallback(() => {
    privacyConsentService.acceptEssentialOnly();
  }, []);

  const acceptRecommended = useCallback(() => {
    privacyConsentService.acceptRecommended();
  }, []);

  const acceptAll = useCallback(() => {
    privacyConsentService.acceptAll();
  }, []);

  const clearLocalDrafts = useCallback(() => {
    privacyConsentService.clearLocalDrafts();
  }, []);

  const clearConsentAndPreferences = useCallback(() => {
    privacyConsentService.clearConsentAndPreferences();
  }, []);

  const value = useMemo<PrivacyConsentContextValue>(
    () => ({
      consent,
      preferences,
      hasMadeChoice: privacyConsentService.hasMadeChoice(),
      canUseFunctionalStorage: privacyConsentService.canUseFunctionalStorage(),
      canUseAnalytics: privacyConsentService.canUseAnalytics(),
      canUseMarketing: privacyConsentService.canUseMarketing(),
      saveConsent,
      acceptEssentialOnly,
      acceptRecommended,
      acceptAll,
      clearLocalDrafts,
      clearConsentAndPreferences,
    }),
    [
      consent,
      preferences,
      saveConsent,
      acceptEssentialOnly,
      acceptRecommended,
      acceptAll,
      clearLocalDrafts,
      clearConsentAndPreferences,
    ]
  );

  return <PrivacyConsentContext.Provider value={value}>{children}</PrivacyConsentContext.Provider>;
}

export function usePrivacyConsent() {
  const context = useContext(PrivacyConsentContext);

  if (!context) {
    throw new Error("usePrivacyConsent must be used within PrivacyConsentProvider");
  }

  return context;
}
